import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  CardInfo,
  SelectLogisticShipmentStatus,
  SelectSaleOrderStatus,
  SelectStatus,
} from '@/components'
import { ModalFinanceValidation } from '@/components/forms/finance-form/components'
import { Text, WorkspacesIcon } from '@mercai/clever'
import { ModalInvoiceWarning } from './components'

import { onErrorMessage } from '@/helpers'
import {
  QUERIES_ADMIN,
  useOrderCenterSaleOrderUpdate,
  useUpdateProfile,
} from '@/services'
import {
  buyerProfileOptions,
  invoiceOptions,
  paymentOptions,
} from './card-order-statuses.constants'

import { CardInfoItemsProps } from '@/components/card-info/card-info.types'
import { FinanceValidationFormEnum } from '@/components/forms/finance-form/components/modal-finance-validation/modal-finance-validation.type'
import {
  OrderInvoiceProps,
  OrderInvoiceStatusEnum,
  OrderPaymentProps,
  OrderPaymentStatusEnum,
  ProfileProps,
} from '@/types'
import type { CardOrderStatusesProps } from './card-order-statuses.type'

import styles from './card-order-statuses.module.css'

export const CardOrderStatuses = ({
  isLoading,
  saleOrder,
}: CardOrderStatusesProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('componentCardsOrderCenterShow')

  const queryClient = useQueryClient()

  const [isOpenInvoiceWarningModal, setIsOpenInvoiceWarningModal] =
    useState(false)

  const [financeValidationFormOpen, setFinanceValidationFormOpen] = useState<
    FinanceValidationFormEnum | undefined
  >(undefined)
  const [selectedPaymentIndex, setSelectedPaymentIndex] = useState<number>()

  const size = 'small'

  const { mutateAsync } = useOrderCenterSaleOrderUpdate(saleOrder?.uuid)
  const { mutateAsync: profileMutateAsync } = useUpdateProfile()

  const onUpdate = (success: boolean) => {
    if (success) {
      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.ORDER_CENTER.module, saleOrder?.uuid],
      })
    } else {
      onErrorMessage(
        t('orderStatusesErrorMessageTitle'),
        t('orderStatusesErrorMessageDescription'),
      )
    }
  }

  const onUpdateBuyerProfileStatus = async (
    taxStatus: ProfileProps['taxStatus'],
  ) => {
    if (!saleOrder?.buyer) return

    const response = await profileMutateAsync({
      uuid: saleOrder.buyer?.uuid || '',
      taxStatus,
    })

    onUpdate(response.success)
  }

  const onUpdateInvoiceStatus = async (
    status: OrderInvoiceProps['status'],
    index: number,
  ) => {
    if (!saleOrder?.invoices) return

    const invoices: OrderInvoiceProps[] = []

    saleOrder.invoices.forEach((invoice, i) => {
      invoices.push({
        uuid: invoice.uuid,
        status: i === index ? status : invoice.status,
      } as OrderInvoiceProps)
    })

    const response = await mutateAsync({
      invoices,
    })

    onUpdate(response.success)
  }

  const currencySaleOrder = saleOrder?.payments?.[0]?.currency
  const dollarExchangeRate = saleOrder?.payments?.[0]?.dollarExchangeRate // verifify

  const getCurrencyValue = (value: string = '0') => {
    if (currencySaleOrder === 'USD') {
      return +value
    }

    if (currencySaleOrder === 'MXN') {
      return Number(value) * Number(dollarExchangeRate || 0)
    }

    return 0
  }

  const validatePayment = (
    keyIndex: number,
  ): { isValid: boolean; error?: FinanceValidationFormEnum } => {
    const totalOrderPrice = getCurrencyValue(saleOrder?.totalOrderPrice)
    const totalPaid = getCurrencyValue(saleOrder?.totalPaidValue)

    if (totalOrderPrice > totalPaid) {
      return {
        isValid: false,
        error: FinanceValidationFormEnum['total-ammount'],
      }
    }

    if (!saleOrder?.payments?.[keyIndex]?.proof) {
      return {
        isValid: false,
        error: FinanceValidationFormEnum['payment-proof'],
      }
    }

    return { isValid: true }
  }

  const handleSavePaymentStatus = async (
    status: OrderPaymentProps['status'],
    index: number,
  ) => {
    if (!saleOrder?.payments?.[index]) return

    const payments: OrderPaymentProps[] = []

    saleOrder.payments.forEach((payment, i) => {
      payments.push({
        uuid: payment.uuid,
        status: i === index ? status : payment.status,
      } as OrderPaymentProps)
    })

    const response = await mutateAsync({
      payments,
    })

    onUpdate(response.success)
  }

  const onUpdatePaymentStatus = ({
    keyIndex,
    value,
  }: {
    keyIndex: number
    value: keyof typeof OrderPaymentStatusEnum
  }) => {
    if (value === OrderPaymentStatusEnum.paid) {
      const { isValid, error } = validatePayment(keyIndex)

      if (isValid) {
        handleSavePaymentStatus(value, keyIndex)
        return
      }

      setSelectedPaymentIndex(keyIndex)
      setFinanceValidationFormOpen(FinanceValidationFormEnum[error!])
      return
    }
    handleSavePaymentStatus(value, keyIndex)
  }

  const getOrderData = (): CardInfoItemsProps[] => {
    const data: CardInfoItemsProps[] = []

    data.push({
      label: t('orderStatusesOrder'),
      value: <SelectSaleOrderStatus saleOrder={saleOrder} size={size} />,
    })

    return data
  }

  const getPaymentsData = (): CardInfoItemsProps[] => {
    const data: CardInfoItemsProps[] = []

    const handleCloseValidationModal = async (forceChange?: boolean) => {
      if (forceChange && selectedPaymentIndex !== undefined) {
        handleSavePaymentStatus('paid', selectedPaymentIndex)
        setSelectedPaymentIndex(undefined)
      }

      setFinanceValidationFormOpen(undefined)
    }

    if (!saleOrder || !saleOrder?.payments) return []

    saleOrder.payments.forEach((payment, index) => {
      const showParcelLabel = ['partial'].includes(payment?.form)

      const parcelLabel = `(${payment?.instalment}/${saleOrder.payments?.length})`

      data.push({
        label: (
          <div className={styles['status-label']}>
            <Text variant="text-base">
              <strong>{t('orderStatusesPayment')}</strong>
            </Text>
            <Text variant="text-small" className={styles.description}>
              <strong>
                ({tEnums(`finance_payment_form_${payment?.form}`)}
                {showParcelLabel && ` ${parcelLabel}`}):
              </strong>
            </Text>
          </div>
        ),
        value: (
          <div>
            <SelectStatus
              size={size}
              status={payment.status}
              options={paymentOptions(tEnums)}
              onChange={(status) =>
                onUpdatePaymentStatus({
                  keyIndex: index,
                  value: status as keyof typeof OrderPaymentStatusEnum,
                })
              }
            />

            {!!financeValidationFormOpen && (
              <ModalFinanceValidation
                isOpen={!!financeValidationFormOpen}
                form={financeValidationFormOpen}
                saleOrderUuid={saleOrder?.uuid}
                onClose={handleCloseValidationModal}
              />
            )}
          </div>
        ),
      })
    })

    return data
  }

  const getInvoicesData = (): CardInfoItemsProps[] => {
    const data: CardInfoItemsProps[] = []

    const onUpdateStatus = async ({
      status,
      index,
    }: {
      status: OrderInvoiceProps['status']
      index: number
    }) => {
      await onUpdateInvoiceStatus(status as OrderInvoiceProps['status'], index)
    }

    if (!saleOrder || !saleOrder?.invoices) return []

    saleOrder.invoices.forEach((invoice, index) => {
      data.push({
        label: (
          <div className={styles['status-label']}>
            <Text variant="text-base">
              <strong>
                {t('orderStatusesInvoice')}
                {saleOrder?.invoices?.length <= 1 && ':'}
              </strong>
            </Text>
            <Text variant="text-base" className={styles.description}>
              {saleOrder?.invoices?.length > 1 && (
                <strong> {index + 1}:</strong>
              )}
            </Text>
          </div>
        ),
        value: (
          <div>
            <SelectStatus
              size={size}
              status={invoice.status}
              options={invoiceOptions(tEnums)}
              onChange={async (status) => {
                if (
                  status === OrderInvoiceStatusEnum.uploaded &&
                  (!invoice.pdfFile || !invoice.xmlFile)
                ) {
                  setIsOpenInvoiceWarningModal(true)
                  return
                }

                onUpdateStatus({
                  status: status as OrderInvoiceStatusEnum,
                  index,
                })
              }}
            />

            <ModalInvoiceWarning
              saleOrderUuid={saleOrder?.uuid}
              invoice={invoice}
              isOpen={isOpenInvoiceWarningModal}
              onSaveAfterUpload={async () => {
                await onUpdateStatus({
                  status: OrderInvoiceStatusEnum.uploaded,
                  index,
                })
                setIsOpenInvoiceWarningModal(false)
              }}
              onClose={() => {
                setIsOpenInvoiceWarningModal(false)
              }}
            />
          </div>
        ),
      })
    })

    return data
  }

  const getShipmentsData = (): CardInfoItemsProps[] => {
    const data: CardInfoItemsProps[] = []

    if (!saleOrder || !saleOrder?.shipments) return []

    saleOrder.shipments.forEach((shipment, index) => {
      data.push({
        label: (
          <div className={styles['status-label']}>
            <Text variant="text-base">
              <strong>
                {t('orderStatusesLogistic')}
                {saleOrder?.shipments?.length <= 1 && ':'}
              </strong>
            </Text>
            <Text variant="text-base" className={styles.description}>
              {saleOrder?.shipments?.length > 1 && (
                <strong> {index + 1}:</strong>
              )}
            </Text>
          </div>
        ),
        value: (
          <SelectLogisticShipmentStatus
            shipment={shipment}
            queryKeys={[[QUERIES_ADMIN.ORDER_CENTER.module, saleOrder?.uuid]]}
            saleOrderUuid={saleOrder?.uuid}
            size="small"
          />
        ),
      })
    })

    return data
  }

  return (
    <CardInfo
      isLoading={isLoading}
      icon={<WorkspacesIcon />}
      title={t('orderStatusesTitle')}
      items={[
        ...getOrderData(),
        {
          label: t('orderStatusesBuyerProfile'),
          value: (
            <SelectStatus
              size={size}
              status={saleOrder?.buyer?.taxStatus}
              options={buyerProfileOptions(tEnums)}
              onChange={async (status) =>
                await onUpdateBuyerProfileStatus(
                  status as ProfileProps['taxStatus'],
                )
              }
            />
          ),
        },
        ...getPaymentsData(),
        ...getInvoicesData(),
        ...getShipmentsData(),
      ]}
    />
  )
}
